import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import classes from './patientAdd.module.scss';
import InputField from '../../../_common/Form/TextFields/InputField';
import Button from '../../../_common/Form/Buttons/Button';
import DOBPicker from '../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import Select from '../../../_common/Form/Selects/Select/Select';
import utils from '../../../../utils/utils';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import api from '../../../../api/api';
import accountHelpers from '../../../../utils/accountHelpers';
import Loader from '../../../_common/Loader/Loader';
import { CardIcon, insuranceTemplate, stateOptions } from '../../../../utils/data/data';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete.svg';
import AddNewCardModal from '../../Team/TeamProfile/AddNewCardModal/AddNewCardModal';
import DeleteCardModal from '../../Team/TeamProfile/DeleteCardModal/DeleteCardModal';
import Checkbox from '../../../_common/Form/Checkbox/Checkbox';

const patientInfoLeftBlockTemplate = [
  {
    path: 'firstName',
    label: 'First Name',
    type: 'input',
    validation: 'required',
    show: true,
  },
  {
    path: 'lastName',
    label: 'Last Name',
    type: 'input',
    validation: 'required',
    show: true,
  },
  {
    path: 'dob',
    label: 'Date of Birth',
    type: 'calendar',
    validation: 'date',
    show: true,
  },
  {
    path: 'hub',
    label: 'Hub',
    type: 'input',
    show: true,
  },
  {
    path: 'sex',
    label: 'Sex',
    type: 'select',
    options: ['Male', 'Female', 'Other'],
    show: true,
  },
  {
    path: 'phone',
    label: 'Phone',
    type: 'input',
    validation: 'phone',
    show: true,
  },
  {
    path: 'phone2',
    label: 'Phone',
    type: 'input',
    validation: 'phone',
  },
  {
    path: 'phone3',
    label: 'Phone',
    type: 'input',
    validation: 'phone',
  },
  {
    path: 'email',
    label: 'Email',
    type: 'input',
    validation: 'email',
    show: true,
  },
  {
    path: 'patientWarning',
    label: 'Patient Warning',
    type: 'input',
    show: true,
  },
];

const patientInfoRightBlockTemplate = [
  {
    path: 'conditions',
    label: 'Co-morbid Conditions',
    type: 'input',
    show: true,
  },
  {
    path: 'allergies',
    label: 'Allergies',
    type: 'input',
    show: true,
  },
  {
    path: 'addressStreet',
    label: 'Street',
    type: 'input',
    show: true,
  },
  {
    path: 'addressCity',
    label: 'City',
    type: 'input',
    show: true,
  },
  {
    path: 'addressState',
    label: 'State',
    type: 'select',
    options: stateOptions,
    show: true,
  },
  {
    path: 'addressZipCode',
    label: 'ZipCode',
    type: 'input',
    validation: 'number',
    show: true,
  },
  {
    path: 'address2Street',
    label: 'Street',
    type: 'input',
    show: false,
  },
  {
    path: 'address2City',
    label: 'City',
    type: 'input',
    show: false,
  },
  {
    path: 'address2State',
    label: 'State',
    type: 'select',
    options: stateOptions,
    show: false,
  },
  {
    path: 'address2ZipCode',
    label: 'ZipCode',
    type: 'input',
    validation: 'number',
    show: false,
  },
  {
    path: 'address3Street',
    label: 'Street',
    type: 'input',
    show: false,
  },
  {
    path: 'address3City',
    label: 'City',
    type: 'input',
    show: false,
  },
  {
    path: 'address3State',
    label: 'State',
    type: 'select',
    options: stateOptions,
    show: false,
  },
  {
    path: 'address3ZipCode',
    label: 'ZipCode',
    type: 'input',
    validation: 'number',
    show: false,
  },
];

const rightBlockTemplate = [
  {
    path: 'location',
    label: 'Location',
    type: 'select',
    options: stateOptions,
    show: true,
  },
  {
    path: 'emergencyContact',
    label: 'Emergency Contact',
    type: 'input',
    show: true,
  },
  {
    path: 'pouch',
    label: 'Pouch',
    type: 'checkbox',
    show: true,
  },
];

const PatientAdd = ({ edit }) => {
  const params = useParams();
  let patientId = params?.patientId;
  const navigate = useNavigate();
  const { menu } = useSelector((store) => store.menu);
  const cancelHref = menu.find(({ name }) => name === 'Patients').path;

  const [patientInfo, setPatientInfo] = useState({});
  const [errors, setErrors] = useState({});
  const [phoneCount, setPhoneCount] = useState('');
  const [addressCount, setAddressCount] = useState('');
  const [deletedCardId, setDeletedCardId] = useState(null);
  const [modalLoad, setModalLoad] = useState(false);
  const [isOpenNewCardModal, setOpenNewCardModal] = useState(false);

  const [patientInfoLeftBlock, setPatientInfoLeftBlock] = useState(patientInfoLeftBlockTemplate);
  const [patientInfoRightBlock, setPatientInfoRightBlock] = useState(patientInfoRightBlockTemplate);

  const [notifyRxScheduled, setNotifyRxScheduled] = useState(false);
  const [notifyShipped, setNotifyShipped] = useState(false);

  const [patientLoad, patientLoadToggle] = useState(false);
  const [saveLoading, saveLoadingToggle] = useState(false);

  const { hubIsDisable, defaultHubValue } = useMemo(() => {
    const key = {
      Health: 'healthUser',
      Physician: 'physicians',
    }[accountHelpers.getUser().role];

    return {
      hubIsDisable: accountHelpers.isInOneOfRoles(['Physician']),
      defaultHubValue: accountHelpers.getUser()[key]?.[0]?.hubId || '',
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (patientId) {
      (async () => {
        patientLoadToggle(true);
        try {
          const { data: { data } } = await api.getPatients({ patientId });
          const info = { ...data.response[0], dob: moment(data.response[0].dob).format('MM/DD/YYYY') };

          const patient = { ...info };
          if (info.Prim) {
            insuranceTemplate.forEach(({ path, backPath }) => {
              patient[`prim${path}`] = info.Prim[backPath];
            });
          }

          if (info.Sec) {
            insuranceTemplate.forEach(({ path, backPath }) => {
              patient[`sec${path}`] = info.Sec[backPath];
            });
          }

          delete patient.createdAt;
          delete patient.Prim;
          delete patient.Sec;

          _.forEach(patient, (val, key) => {
            const keyNumber = +key.replace(new RegExp('[^\\d]', 'g'), '');

            if ((key === 'phone2' || key === 'phone3') && val) {
              const dependentKey = key === 'phone3' ? 'phone2' : '';
              setPhoneCount(keyNumber);

              setPatientInfoLeftBlock((prev) => prev.map((p) => (
                (p.path === key || p.path === dependentKey) ? { ...p, show: true } : p)));
            } else if (key.includes('address') && keyNumber && val) {
              const dependentKeys = [];
              let addressKeys = ['address2Street', 'address2City',
                'address2State', 'address2ZipCode'];

              if (keyNumber === 3) {
                addressKeys = [...addressKeys, 'address3Street',
                  'address3City', 'address3State', 'address3ZipCode'];
              }

              addressKeys.forEach((path) => {
                if (key.includes(`address${keyNumber}`)) dependentKeys.push(path);
              });

              setAddressCount(keyNumber);

              setPatientInfoRightBlock((prev) => prev.map((p) => (
                (dependentKeys.includes(p.path)) ? { ...p, show: true } : p)));
            } else if (key === 'hub') {
              setPatientInfoLeftBlock((prev) => prev.map((p) => (
                p.path === key ? { ...p, show: true, disabled: hubIsDisable } : p)));
            }
          });

          setNotifyRxScheduled(patient.rxScheduledPhone || patient.rxScheduledEmail);
          setNotifyShipped(patient.rxShippedPhone || patient.rxShippedEmail);
          setPatientInfo(patient);
        } catch (e) {
          console.warn(e);
        }

        patientLoadToggle(false);
      })();
    } else {
      setPatientInfo((prev) => ({
        ...prev,
        location: 'Los Angeles',
        hub: defaultHubValue,
      }));
    }
  }, []);

  const changePatient = (path, value) => {
    setErrors((prev) => ({ ...prev, [path]: '' }));

    setPatientInfo((prev) => ({
      ...prev,
      [path]: value,
    }));
  };

  const changeNotifyRxScheduled = (toggle) => {
    setNotifyRxScheduled(toggle);

    setPatientInfo((prev) => ({
      ...prev,
      rxScheduledPhone: toggle,
      rxScheduledEmail: toggle,
    }));
  };

  const changeNotifyShipped = (toggle) => {
    setNotifyShipped(toggle);

    setPatientInfo((prev) => ({
      ...prev,
      rxShippedPhone: toggle,
      rxShippedEmail: toggle,
    }));
  };

  const onKeyPress = (e, validationType, isPaste) => {
    if (validationType === 'number') {
      utils.keyPressOnlyNumber(e, isPaste);
    } else if (validationType === 'phone') {
      utils.keyPressPhoneNumber(e, isPaste);
    }
  };

  const closeAddNewCardModal = () => {
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('width');
    document.body.ontouchmove = () => true;

    setOpenNewCardModal(false);
  };

  const addPhone = () => {
    const nextPhone = !phoneCount ? 2 : 3;
    setPhoneCount(nextPhone);
    setPatientInfoLeftBlock((prev) => prev.map((p) => (p.path === `phone${nextPhone}` ? { ...p, show: true } : p)));
  };

  const addAddress = () => {
    const nextAddress = !addressCount ? 2 : 3;
    setAddressCount(nextAddress);
    setPatientInfoRightBlock((prev) => prev.map(
      (p) => (p.path.includes(`address${nextAddress}`) ? { ...p, show: true } : p),
    ));
  };

  const onSave = async () => {
    let hasError = false;

    setErrors({});

    patientInfoLeftBlockTemplate.forEach(({ path, validation }) => {
      if (validation === 'required' && !patientInfo[path]) {
        setErrors((prev) => ({ ...prev, [path]: 'Field is required' }));
        hasError = true;
      } else if (validation === 'email' && patientInfo[path] && !utils.isEmail(patientInfo[path])) {
        setErrors((prev) => ({ ...prev, [path]: 'Invalid email' }));
        hasError = true;
      } else if (validation === 'date' && patientInfo[path] && patientInfo[path].length !== 10) {
        setErrors((prev) => ({ ...prev, [path]: 'Invalid date' }));
        hasError = true;
      } else if (path === 'phone' && !patientInfo.phone && (patientInfo.rxScheduledPhone || patientInfo.rxShippedPhone)) {
        setErrors((prev) => ({ ...prev, phone: 'Field is required' }));
        hasError = true;
      } else if (path === 'email' && !patientInfo.email && (patientInfo.rxScheduledEmail || patientInfo.rxShippedEmail)) {
        setErrors((prev) => ({ ...prev, email: 'Field is required' }));
        hasError = true;
      }
    });

    if (!hasError) {
      saveLoadingToggle(true);

      const data = {
        userId: accountHelpers.getUser().id,
        ...patientInfo,
      };

      try {
        if (!edit) {
          const { data: { response } } = await api.createPatient(data);

          patientId = response.id;
        } else {
          await api.updatePatient(data);
        }

        navigate(`/patients/${patientId}`);

        toast.success(`Patient successfully ${!edit ? 'created' : 'updated'}`);
      } catch (e) {
        toast.error('Something went wrong');
        console.warn(e);
      }

      saveLoadingToggle(false);
    } else {
      toast.error('Please correct all errors');
    }
  };

  const deleteCard = async (idCard) => {
    setModalLoad(true);
    const deletedCard = patientInfo.authorizeNet.find((a) => a.customerPaymentProfileId === idCard);

    await api.deleteCard({
      profileId: deletedCard.profileId, profilePaymentId: deletedCard.customerPaymentProfileId,
    });

    setPatientInfo((prev) => (
      { ...prev, authorizeNet: prev.authorizeNet.filter((a) => a.customerPaymentProfileId !== idCard) }));

    toast.success('Removed selected card');

    setModalLoad(false);
    setDeletedCardId(null);
  };

  const saveCard = async (cardData) => {
    setModalLoad(true);
    try {
      const data = {
        cardNumber: cardData.cardNumber.replace(/ /g, ''),
        cardCVC: cardData.cardCVC.replace(/ /g, ''),
        cardExpiry: cardData.cardExpiry.replace(/\s/g, '')
          .replace(/\//g, ''),
      };

      if (patientInfo.profileId) {
        data.profileId = patientInfo.profileId;
      } else {
        data.patientId = patientId;
      }
      const { data: { authorizeNet } } = await api.createCard({ ...data });

      setPatientInfo((prev) => ({
        ...prev,
        authorizeNet,
      }));

      toast.success('Created new card');

      setModalLoad(false);

      setOpenNewCardModal(false);
    } catch (e) {
      console.warn(e);

      toast.error('Something wrong ');
    }
  };

  return (
    <div className={classes.wrapper}>
      <h2>{!edit ? 'Add Patient' : 'Edit Patient'}</h2>

      {patientLoad
        ? (
          <div className={classes.loader_wrapper}>
            <Loader />
          </div>
        )

        : (
          <div className={classes.fields_container_wrapper}>
            <div className={classes.fields_container}>
              <div className={classes.top_block}>
                <div className={classes.left_block}>
                  {patientInfoLeftBlock.map(({
                    path, label, type, validation, options, show, disabled,
                  }) => (
                    show && (
                      <div key={path} className={classes.info_field_wrapper}>
                        {type === 'calendar'
                          ? (
                            <DOBPicker
                              label={label}
                              date={patientInfo[path] || ''}
                              onChange={(date) => changePatient(path, date)}
                              error={errors[path]}
                              autoValidation={false}
                            />
                          )
                          : type === 'select'
                            ? (
                              <Select
                                label={label}
                                onChange={(option) => changePatient(path, option)}
                                currentOption={patientInfo[path] || ''}
                                options={options}
                                zIndex={30}
                              />
                            )
                            : (
                              <InputField
                                label={label}
                                value={patientInfo[path] || ''}
                                onChange={(value) => !disabled && changePatient(path, value)}
                                error={errors[path]}
                                disabled={disabled}
                                onKeyPress={(e) => onKeyPress(e, validation)}
                                onPaste={(e) => onKeyPress(e, validation, true)}
                              />
                            )}

                        {path === `phone${phoneCount}` && phoneCount < 3 && (
                        <div
                          role="button"
                          tabIndex="0"
                          className={classes.add_phone_button}
                          onClick={addPhone}
                        >
                          <PlusIcon />

                          Add Additional Phone
                        </div>
                        )}
                      </div>
                    )
                  ))}
                </div>

                <div className={classes.right_block}>
                  {patientInfoRightBlock.map(({
                    path, type, label, options, validation, show,
                  }) => (
                    show && (
                      <div
                        key={path}
                        className={classes.info_field_wrapper}
                      >
                        {type === 'select'
                          ? (
                            <Select
                              label={label}
                              onChange={(option) => changePatient(path, option)}
                              currentOption={patientInfo[path] || ''}
                              options={options}
                              zIndex={30}
                            />
                          )
                          : (
                            <InputField
                              label={label}
                              value={patientInfo[path] || ''}
                              onChange={(value) => changePatient(path, value)}
                              error={errors[path]}
                              onKeyPress={(e) => onKeyPress(e, validation)}
                              onPaste={(e) => onKeyPress(e, validation, true)}
                            />
                          )}

                        {path.includes('ZipCode')
                    && +path.replace(new RegExp('', 'g'), '') < addressCount
                    && <div className={classes.line} />}

                        {path.includes(`address${addressCount}ZipCode`) && addressCount < 3 && (
                        <div
                          role="button"
                          tabIndex="0"
                          className={classes.add_phone_button}
                          onClick={addAddress}
                        >
                          <PlusIcon />

                          Add Additional Address
                        </div>
                        )}
                      </div>
                    )
                  ))}
                </div>
              </div>

              <div className={classes.bottom_block}>
                <div className={classes.left_block}>
                  <h3>Primary Insurance</h3>

                  {insuranceTemplate.map(({
                    path, label, type, options,
                  }) => (
                    <div key={path} className={classes.info_field_wrapper}>
                      {type === 'select'
                        ? (
                          <Select
                            label={label}
                            onChange={(option) => changePatient(`prim${path}`, option)}
                            currentOption={patientInfo[`prim${path}`] || ''}
                            options={options}
                            zIndex={30}
                          />
                        )
                        : (
                          <InputField
                            label={label}
                            value={patientInfo[`prim${path}`] || ''}
                            onChange={(value) => changePatient(`prim${path}`, value)}
                          />
                        )}
                    </div>
                  ))}
                </div>

                <div className={classes.right_block}>
                  <h3>Secondary Insurance</h3>

                  {insuranceTemplate.map(({
                    path, label, type, options,
                  }) => (
                    <div key={path} className={classes.info_field_wrapper}>
                      {type === 'select'
                        ? (
                          <Select
                            label={label}
                            onChange={(option) => changePatient(`sec${path}`, option)}
                            currentOption={patientInfo[`sec${path}`] || ''}
                            options={options}
                            zIndex={30}
                          />
                        )
                        : (
                          <InputField
                            label={label}
                            value={patientInfo[`sec${path}`] || ''}
                            onChange={(value) => changePatient(`sec${path}`, value)}
                          />
                        )}
                    </div>
                  ))}
                </div>
              </div>

              {edit && (
                <div className={classes.card_payment}>
                  <h3>Card</h3>
                  {!!patientInfo?.authorizeNet?.length && patientInfo?.authorizeNet.map((a) => (
                    <div key={a?.customerPaymentProfileId} className={classes.cards}>
                      <span>{CardIcon[a?.payment?.creditCard?.cardType]}</span>

                      <span>{a?.payment?.creditCard?.cardNumber}</span>

                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => setDeletedCardId(a?.customerPaymentProfileId)}
                      >
                        <DeleteIcon />
                      </span>

                    </div>
                  ))}
                  <p onClick={() => setOpenNewCardModal(true)}>+ Add Credit Card</p>
                </div>
              )}

              <div className={classes.btns_wrapper}>
                <Button
                  loading={saveLoading}
                  title={!edit ? 'Create Patient' : 'Update Patient'}
                  addButton={!edit}
                  reverseColor
                  onClick={onSave}
                />

                <Button
                  title="Cancel"
                  clearButton
                  onClick={() => navigate(cancelHref)}
                />
              </div>
            </div>

            <div className={classes.location_select}>
              {rightBlockTemplate.map(({
                path, type, label, options, show,
              }) => (
                show && (
                  <div
                    key={path}
                    className={classes.info_field_wrapper}
                  >
                    {type === 'select'
                      ? (
                        <Select
                          label={label}
                          onChange={(option) => changePatient(path, option)}
                          currentOption={patientInfo[path] || ''}
                          options={options}
                          zIndex={30}
                          hideClearButton
                        />
                      )

                      : type === 'checkbox'
                        ? (
                          <Checkbox
                            label={label}
                            onChange={(checked) => changePatient(path, checked)}
                            checked={patientInfo[path]}
                          />
                        )

                        : (
                          <InputField
                            label={label}
                            value={patientInfo[path] || ''}
                            onChange={(value) => changePatient(path, value)}
                          />
                        )}
                  </div>
                )))}

              <div>
                <Checkbox
                  label="Notify Patient: Rx Scheduled"
                  onChange={(checked) => changeNotifyRxScheduled(checked)}
                  checked={notifyRxScheduled}
                />

                {notifyRxScheduled && (
                  <>
                    <div className={classes.scheduled_field_wrapper}>
                      <Checkbox
                        checked={patientInfo.rxScheduledPhone}
                        onChange={(checked) => changePatient('rxScheduledPhone', checked)}
                      />

                      <InputField
                        label="Phone"
                        value={patientInfo.phone || ''}
                        error={patientInfo.rxScheduledPhone && errors.phone}
                        onChange={(value) => changePatient('phone', value)}
                      />
                    </div>

                    <div className={classes.scheduled_field_wrapper}>
                      <Checkbox
                        checked={patientInfo.rxScheduledEmail}
                        onChange={(checked) => changePatient('rxScheduledEmail', checked)}
                      />

                      <InputField
                        label="Email"
                        value={patientInfo.email || ''}
                        error={patientInfo.rxScheduledEmail && errors.email}
                        onChange={(value) => changePatient('email', value)}
                      />
                    </div>
                  </>
                )}
              </div>

              <div>
                <Checkbox
                  label="Notify Patient: Rx Shipped"
                  onChange={(checked) => changeNotifyShipped(checked)}
                  checked={notifyShipped}
                />

                {notifyShipped && (
                  <>
                    <div className={classes.scheduled_field_wrapper}>
                      <Checkbox
                        checked={patientInfo.rxShippedPhone}
                        onChange={(checked) => changePatient('rxShippedPhone', checked)}
                      />

                      <InputField
                        label="Phone"
                        value={patientInfo.phone || ''}
                        error={patientInfo.rxShippedPhone && errors.phone}
                        onChange={(value) => changePatient('phone', value)}
                      />
                    </div>

                    <div className={classes.scheduled_field_wrapper}>
                      <Checkbox
                        checked={patientInfo.rxShippedEmail}
                        onChange={(checked) => changePatient('rxShippedEmail', checked)}
                      />

                      <InputField
                        label="Email"
                        value={patientInfo.email || ''}
                        error={patientInfo.rxShippedEmail && errors.email}
                        onChange={(value) => changePatient('email', value)}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      {isOpenNewCardModal && (
      <AddNewCardModal
        onClose={closeAddNewCardModal}
        isOpen={isOpenNewCardModal}
        loading={modalLoad}
        saveCard={saveCard}
      />
      )}

      <DeleteCardModal
        isOpen={!!deletedCardId}
        onClose={() => setDeletedCardId(null)}
        onDeleteClick={() => deleteCard(deletedCardId)}
        loading={modalLoad}
      />
    </div>
  );
};

PatientAdd.propTypes = {
  edit: PropTypes.bool,
};

PatientAdd.defaultProps = {
  edit: false,
};

export default PatientAdd;
